/**
 * Hook to track and display AI lock duration with live updates.
 */

import { useState, useEffect } from 'react';
import type { AiLockInfo } from '../store/aiLockStore';
import { formatLockDuration, getDurationSince, isLockStale } from '../utils/lockDurationUtils';

type LockDurationResult = {
  /** Formatted duration string like "2m 30s" */
  formattedDuration: string;
  /** Whether the lock is stale (no activity for timeout period) */
  isStale: boolean;
  /** Duration in milliseconds since lock started */
  durationMs: number;
};

/**
 * Returns live-updating lock duration information.
 * Updates every second when a lock is active.
 */
export function useLockDuration(
  lockInfo: AiLockInfo | undefined,
  timeoutMinutes: number
): LockDurationResult | null {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!lockInfo) return;

    // Update every second while lock is active
    const interval = setInterval(() => {
      setTick((t) => t + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [lockInfo]);

  if (!lockInfo) return null;

  // tick forces re-render every second for live updates
  void tick;

  const reference = lockInfo.lastActivity ?? lockInfo.lockedAt;
  const durationMs = getDurationSince(reference);

  return {
    formattedDuration: formatLockDuration(durationMs),
    isStale: isLockStale(lockInfo.lastActivity, lockInfo.lockedAt, timeoutMinutes),
    durationMs,
  };
}
